import {ObjectId} from 'bson';
import {
  GetAnalysisRunCredits,
  GetFieldDefinitionAnalysisRunResults,
  GetLatestFieldDefinitionAnalysisRun,
  UpdateFieldDefinitionAnalysisRun,
} from '../../shared/api/definitions/root';
import {createApiQuery, useApiMutation} from './api';
import {useQueryClient} from '@tanstack/react-query';

export const FieldDefinitionAnalysisRunQueryKeys = {
  all: ['fieldDefinitionAnalysisRun'],
  field: ({fieldDefinitionId}: {fieldDefinitionId: ObjectId}) => [
    ...FieldDefinitionAnalysisRunQueryKeys.all,
    'field',
    fieldDefinitionId.toString(),
  ],
  results: (fieldDefinitionRunId: ObjectId) => [
    ...FieldDefinitionAnalysisRunQueryKeys.all,
    'results',
    fieldDefinitionRunId.toString(),
  ],
};

export const useLatestFieldDefinitionAnalysisRun = createApiQuery(
  GetLatestFieldDefinitionAnalysisRun,
  ({fieldDefinitionId}) =>
    FieldDefinitionAnalysisRunQueryKeys.field({fieldDefinitionId}),
  {
    refetchInterval: (query) =>
      query.state.data?.results.some(({status}) => status === 'running')
        ? 5_000
        : 0,
  }
);

export const useUpdateFieldDefinitionAnalysisRun = () => {
  const queryClient = useQueryClient();

  return useApiMutation(UpdateFieldDefinitionAnalysisRun, {
    onSuccess: (updatedRun) => {
      if (!updatedRun) {
        return;
      }

      queryClient.setQueryData(
        FieldDefinitionAnalysisRunQueryKeys.field({
          fieldDefinitionId: updatedRun.fieldDefinition.id,
        }),
        updatedRun
      );
    },
  });
};

export const useGetAnalysisRunResults = createApiQuery(
  GetFieldDefinitionAnalysisRunResults,
  ({fieldDefinitionRunId}) =>
    FieldDefinitionAnalysisRunQueryKeys.results(fieldDefinitionRunId)
);

export const AnalysisRunCreditsQueryKeys = {
  all: ['analysisRunCredits'],
  field: ({fieldDefinitionId}: {fieldDefinitionId: ObjectId}) => [
    ...AnalysisRunCreditsQueryKeys.all,
    fieldDefinitionId.toString(),
  ],
  testLists: ({
    fieldDefinitionId,
    testListIds,
  }: {
    fieldDefinitionId: ObjectId;
    testListIds: ObjectId[];
  }) => [
    ...AnalysisRunCreditsQueryKeys.field({fieldDefinitionId}),
    // don't want to mutate original array
    ...[...testListIds].sort().map(String),
  ],
};

export const useGetAnalysisRunCredits = createApiQuery(
  GetAnalysisRunCredits,
  ({fieldDefinitionId, testListIds}) =>
    AnalysisRunCreditsQueryKeys.testLists({
      fieldDefinitionId,
      testListIds,
    })
);
